import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { PATH_CLIENT_ACCOUNT_EDIT } from 'routing/path'
import PageHeader from 'components/page-header'
import ValidatedField from 'components/controls/validated-field';
import ValidatedSelectField from 'components/controls/validated-select-field';
import { showSuccessNotification, showErrorNotification } from "actions/action-complete-notificator";
import { useGetAllClientsWithoutDetailsQuery } from 'actions/client/client-api';
import { useGetAccountQuery, useAddAccountMutation, useSaveAccountMutation, useResendInvitationEmailMutation } from 'actions/client/client-account-api';
import { translationApi  } from 'actions/translation-api';
import getUserStatusDescription, { USER_STATUS_INACTIVE, USER_STATUS_ACTIVE } from 'data/enums/user-status-enum';
import { tryParseInt } from 'utils/number-utils';

const ClientAccountEdit = () => {
	const navigate = useNavigate();
	const params = useParams();
	const clientAccountIdString = params.clientAccountId;
	const clientAccountId = tryParseInt(clientAccountIdString);

	const { data: clientAccount, isLoading: loadAccountInProgress, isFetching: fetchAccountInProgress } = useGetAccountQuery(clientAccountId, clientAccountId ? { refetchOnMountOrArgChange: true } : { skip: true } );
	const [ addAccount, { isLoading: addAccountInProgress }] = useAddAccountMutation();
	const [ saveAccount, { isLoading: saveAccountInProgress }] = useSaveAccountMutation();
	const [ resendInvitationEmail, { isLoading: resendInvitationEmailInProgress }] = useResendInvitationEmailMutation();
	const { data: clientList } = useGetAllClientsWithoutDetailsQuery(null, { refetchOnMountOrArgChange: true });
	const translations = useSelector(translationApi.endpoints.getTranslations.select()).data ?? {};
	
	if (clientAccountIdString && !loadAccountInProgress && !fetchAccountInProgress && !clientAccount) {
		// facility load finished, but account not found
		navigate("/404");
		return;
	}

	const getPageHeader = () => {
		if (!clientAccountId) {
			// new client account
			return <PageHeader title={translations.newClientAccount} breadcrumbs={[]} breadcrumbsCurrentPage={translations.newClientAccount} />
		}
	
		// existing client account
		if (loadAccountInProgress || fetchAccountInProgress) {
			return <PageHeader title="..." breadcrumbs={[]} breadcrumbsCurrentPage="..." />
		}

		return <PageHeader title={`${clientAccount.firstName} ${clientAccount.lastName}`} breadcrumbs={[]} breadcrumbsCurrentPage={`${translations.account} ${clientAccount.firstName} ${clientAccount.lastName}`} />
	}

	const initializeClientAccount = clientAccountId && !loadAccountInProgress && !fetchAccountInProgress;
	const initialValues = {
		firstName: initializeClientAccount ? clientAccount.firstName : "",
		lastName: initializeClientAccount ? clientAccount.lastName : "",
		status: initializeClientAccount ? clientAccount.status : USER_STATUS_ACTIVE,
		email: initializeClientAccount ? clientAccount.email : "",
		lastLoginDate: initializeClientAccount ? clientAccount.lastLoginDate : "",
		invitationsSent: initializeClientAccount ? clientAccount.invitationsSent : 0,
		lastInvitationDate: initializeClientAccount ? clientAccount.lastInvitationDate : "",
		connectedClientIds: initializeClientAccount ? clientAccount.connectedClientIds.map(function (clientId) {
			return { clientId: clientId };
		}) : [],
	};

	const userStatusesToDropDownList = [
		[USER_STATUS_ACTIVE, getUserStatusDescription(USER_STATUS_ACTIVE, translations)],
		[USER_STATUS_INACTIVE, getUserStatusDescription(USER_STATUS_INACTIVE, translations)]
	];

	const selectClientPrompt = ['', '[Wybierz]'];
	const clientsToDropDownList = clientList ? clientList.map(function (client) {
		return [ client.clientId, client.name ]
	}) : [];
	clientsToDropDownList.unshift(selectClientPrompt);
	
	Yup.addMethod(Yup.array, 'unique', function(message, mapper = a => a.clientId) {
		return this.test('unique', message, function(list) {
			return list.length  === new Set(list.map(mapper)).size;
		});
	});
	const validationSchema = Yup.object().shape({
		firstName: Yup.string()
			.required(translations.warningFieldRequired),
		lastName: Yup.string()
			.required(translations.warningFieldRequired),
		email: Yup.string()
			.required(translations.warningFieldRequired)
			.email(translations.warningValueInvalid),
		connectedClientIds: Yup.array().of(Yup.object().shape({
			clientId: Yup.number()
				.required(translations.warningFieldRequired)
			}))
			.unique(translations.warningSelectedClientsMustBeUnique)
	});
	
	const submitDisabled = addAccountInProgress || saveAccountInProgress;
	const handleSubmit = async (fields) => {
		const clientAccountToUpsert = {
			clientAccountId: clientAccountId,
			firstName: fields.firstName,
			lastName: fields.lastName,
			status: tryParseInt(fields.status),
			email: fields.email,
			connectedClientIds: fields.connectedClientIds.map(function (client) {
				return tryParseInt(client.clientId, 10);
			})
		};
		
		if (clientAccountId) {
			const result = await saveAccount(clientAccountToUpsert);
			if ('error' in result) {
				showErrorNotification(result.error);
			}
			else {
				showSuccessNotification(translations.changesSaved);
			}
		}
		else {
			const result = await addAccount(clientAccountToUpsert);
			if ('error' in result) {
				showErrorNotification(result.error);
			}
			else {
				showSuccessNotification(translations.accountAdded);
				navigate(PATH_CLIENT_ACCOUNT_EDIT + result.data);
				return;
			}
		}
	}
	
	const resendInvitationEmailDisabled = resendInvitationEmailInProgress;
	const handleResendInvitationEmail = async (setFieldValue) => {
		const result = await resendInvitationEmail({ clientAccountId: clientAccountId });
		if ('error' in result) {
			showErrorNotification(translations.errorSendingEmailFailed);
		}
		else {
			setFieldValue('invitationsSent', result.data.invitationsSent);
			setFieldValue('lastInvitationDate', result.data.lastInvitationDate);
			showSuccessNotification(translations.invitationSent);
		}
	}

	const randomAutoComplete = Math.random().toString(36).replace(/[^a-z]+/g, "").substr(0, 5);
	return (
		<div>
			{ getPageHeader() }
			{ <Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={ async (fields) => { await handleSubmit(fields) }}>
				{props => (
					<Form autoComplete={randomAutoComplete}>
						<div className="col-12 mb-4">
							<div className="card border-light shadow-sm components-section">
								<div className="card-body">
									<h2 className="h5 mb-4">{translations.basicData}</h2>
									<div className="row mb-4">
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="firstName" label={translations.firstName} {...props} />
										</div>
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="lastName" label={translations.lastName} {...props} />
										</div>
									</div>
									<div className="row mb-4">
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="email" label={translations.email} {...props} />
										</div>
										<div className="col-lg-4 col-sm-6">
											<ValidatedSelectField name="status" label={translations.status} keyValueMap={userStatusesToDropDownList} {...props} />
										</div>
									</div>
									{props.values.lastLoginDate === null &&
										<div>
											<h2 className="h5 mb-4">{translations.invitations}</h2>
											<div className="row">
												<div className="col-lg-4 col-sm-6">
													<label>{`${translations.invitationsSent}: ${props.values.invitationsSent}`}</label>
												</div>
												<div className="col-lg-4 col-sm-6">
													<label>{`${translations.lastInvitationDate}: ${props.values.lastInvitationDate ?? translations.noData}`}</label>
												</div>
											</div>
											<div className="row mb-4">
												<div className="col-lg-4 col-sm-6">
													<button id="resendInvitationButton" type="button" className="btn btn-gray-800 me-2" onClick={ async () => { await handleResendInvitationEmail(props.setFieldValue); }} disabled={resendInvitationEmailDisabled ? "disabled" : null}>{translations.resendInvitation}</button>
												</div>
											</div>
										</div>
									}
									<h2 className="h5 mb-4">{translations.clientList}</h2>
									<FieldArray
										name="connectedClientIds"
										render={arrayHelpers => (
											<div>
												{props.errors && props.errors.connectedClientIds && typeof props.errors.connectedClientIds === "string" &&
													<div className="invalid-feedback mb-2" style={{ display: "block" }} >{props.errors.connectedClientIds}</div>
												}
												{props.values.connectedClientIds.map((connectedClientId, index) => (
													<div key={index} className="row mb-4">
														<div className="col-lg-4 col-sm-6">
															<ValidatedSelectField name="clientId" fieldArray="connectedClientIds" fieldArrayIndex={index} keyValueMap={clientsToDropDownList} {...props} />
														</div>
														<div className="col-lg-4 col-sm-6">
															<button type="button" className="btn btn-danger mt-0 mb-0" onClick={() => arrayHelpers.remove(index)}>{translations.delete}</button>
														</div>
													</div>
												))}
												<button id="addClientButton" type="button" className="btn btn-gray-800 me-2" onClick={() => arrayHelpers.push({ clientId: ''})}>{translations.actionAddClient}</button>
											</div>
										)}
									/>
									<div className="row mt-5 mt-lg-5 mb-0 mb-lg-0">
										<div className="col-lg-3 col-md-6">
											<div className="form-group mb-3">
												<button id="saveButton" type="submit" className="btn btn-primary me-2" disabled={submitDisabled ? "disabled" : null} >{translations.actionSave}</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Form>)}
			</Formik>}
		</div>
	)
} 

export default ClientAccountEdit;
